import { useState } from "react";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

const COLORS = ["#C59B27", "#7FB3A6", "#E8A0A0", "#A9B8F0"];
const ts = (period) => (period.length === 4 ? Date.UTC(+period, 6, 1) : Date.UTC(+period.slice(0, 4), +period.slice(5, 7) - 1, 15));
const label = (x) => new Date(x).toLocaleDateString("en-AU", { month: "short", year: "numeric", timeZone: "UTC" });

export const seriesFor = (t, key = "sale_sqm") => {
  const asking = t.history?.asking || [];
  const src = asking.length ? asking[asking.length - 1].source_name : null;
  return {
    recorded: key === "sale_sqm" ? (t.history?.recorded || []).map((p) => ({ x: ts(p.period), y: p.sale_sqm })) : [],
    asking: asking.filter((p) => p.source_name === src && p[key]).map((p) => ({ x: ts(p.period), y: p[key] })),
  };
};

export const hasHistory = (t) => { const s = seriesFor(t); return s.recorded.length + s.asking.length >= 2; };

const fmt = (rent) => (v) => (rent ? `€${Number(v).toFixed(2)}` : `€${Math.round(v).toLocaleString("en-AU")}`);

export const PriceChart = ({ towns }) => {
  const [key, setKey] = useState("sale_sqm");
  const rent = key === "rent_sqm";
  const shown = towns.filter((t) => (rent ? seriesFor(t, key).asking.length > 0 : hasHistory(t)));
  if (!towns.some(hasHistory) && !towns.some((t) => seriesFor(t, "rent_sqm").asking.length)) return null;
  const tab = (k, l) => <button onClick={() => setKey(k)} data-testid={`calc-chart-${k === "rent_sqm" ? "rent" : "sale"}`} className={`px-3 py-1.5 text-xs transition-colors ${key === k ? "bg-[#C59B27] text-[#1C181D]" : "text-[#FAF7F2]/70 hover:text-[#FAF7F2]"}`}>{l}</button>;
  const single = shown.every((t) => seriesFor(t, key).asking.length + seriesFor(t, key).recorded.length < 2);
  return (
    <div data-testid="calc-price-chart" className="mt-8">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="flex border border-[#FAF7F2]/20">{tab("sale_sqm", "Sale price per m²")}{tab("rent_sqm", "Rent per m² per month")}</div>
        <p className="text-sm text-[#FAF7F2]/80" data-testid="calc-chart-note">{rent
          ? `Monthly asking rents (France: Meilleurs Agents average; Italy: midpoint of the official OMI range).${single ? " Trend lines build from next month." : ""}`
          : "Solid lines are current asking prices, updated monthly; dashed lines are official recorded sale prices (France, yearly)."}</p>
      </div>
      {shown.length === 0 ? <p className="text-sm text-[#FAF7F2]/60 h-20" data-testid="calc-chart-empty">{rent ? "No rent data yet for these towns." : "No sale-price history yet for these towns."}</p> : (<>
      <div className="h-72 bg-[#FAF7F2]/[0.04] p-3">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart margin={{ top: 8, right: 16, bottom: 0, left: 8 }}>
            <CartesianGrid stroke="#FAF7F2" strokeOpacity={0.08} />
            <XAxis dataKey="x" type="number" scale="time" domain={["dataMin", "dataMax"]} tickFormatter={label} stroke="#FAF7F2" strokeOpacity={0.5} tick={{ fontSize: 11 }} allowDuplicatedCategory={false} padding={{ left: 24, right: 24 }} />
            <YAxis tickFormatter={fmt(rent)} stroke="#FAF7F2" strokeOpacity={0.5} tick={{ fontSize: 11 }} width={64} domain={["auto", "auto"]} />
            <Tooltip labelFormatter={label} formatter={(v, n) => [`${fmt(rent)(v)}/m²${rent ? " per month" : ""}`, n]} contentStyle={{ background: "#1C181D", border: "none", color: "#FAF7F2", fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {shown.flatMap((t, i) => {
              const s = seriesFor(t, key);
              const c = COLORS[i % COLORS.length];
              return [
                s.recorded.length > 0 && <Line key={`${t.slug}-r`} data={s.recorded} dataKey="y" name={`${t.town} · recorded sales`} stroke={c} strokeDasharray="5 4" dot={{ r: 2 }} isAnimationActive={false} />,
                s.asking.length > 0 && <Line key={`${t.slug}-a`} data={s.asking} dataKey="y" name={`${t.town} · asking${rent ? " rent" : ""}`} stroke={c} strokeWidth={2} dot={{ r: 3 }} isAnimationActive={false} />,
              ].filter(Boolean);
            })}
          </LineChart>
        </ResponsiveContainer>
      </div>
      </>)}
    </div>
  );
};

export const Sparkline = ({ t }) => {
  if (!hasHistory(t)) return null;
  const s = seriesFor(t);
  const one = s.recorded.length >= 2 ? s.recorded : s.asking;
  const ch = Math.round((one[one.length - 1].y / one[0].y - 1) * 100);
  const kind = one === s.recorded ? "recorded sale" : "asking";
  return (
    <div className="flex items-center gap-3" data-testid={`calc-sparkline-${t.slug}`}>
      <div className="h-8 w-28">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={one}><Line dataKey="y" stroke="#3B1B48" strokeWidth={1.5} dot={false} isAnimationActive={false} /><YAxis hide domain={["dataMin", "dataMax"]} /><XAxis dataKey="x" type="number" hide domain={["dataMin", "dataMax"]} /></LineChart>
        </ResponsiveContainer>
      </div>
      <p className="text-[11px] text-[#8C827A]">{kind} €/m² {ch >= 0 ? "+" : ""}{ch}% {label(one[0].x)}–{label(one[one.length - 1].x)}</p>
    </div>
  );
};
